import Service from '@ember/service';
import { service } from '@ember/service';
import { getOwner } from '@ember/application';
import { task, timeout } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import tippy, { hideAll } from 'tippy.js';
import normalizeIdentifier from 'ember-stereo/-private/utils/normalize-identifier';
import { registerDestructor } from '@ember/destroyable';
import { SyncChannel } from '../utils/sync-channel';

export default class PlaybackService extends Service {
  @service stereo;
  @service feeds;
  @service store;
  @service session;

  @tracked _closed = false;
  @tracked blockedSound;
  @tracked blockedPrompt;

  get playerIsActive() {
    if (this._closed) return false;
    return !!(this.stereo.currentSound || this.blockedSound);
  }

  _syncChannel = null;

  constructor() {
    super(...arguments);
    let capabilities = getOwner(this)?.lookup('service:capabilities');
    this._syncChannel = new SyncChannel({
      stereo: this.stereo,
      describeSound: (sound, isPlaying) => this.broadcastSoundState(sound, isPlaying),
      onOpenEpisode: (details) => this.openEpisodeFromRemote(details),
      onGoLive: () => this.goLiveFromRemote(),
      relayTo: capabilities?.hostsPlayer && !capabilities.remoteControl ? capabilities.playerUrl : null,
    });
    this.addStereoMonitors();
    registerDestructor(this, this.teardown.bind(this));
  }

  // ---------------------------------------------------------------------------
  // Cross-tab sync — proxied from SyncChannel
  // ---------------------------------------------------------------------------

  get remoteIsPlaying()  { return this._syncChannel.remoteIsPlaying; }
  get remoteUrl()        { return this._syncChannel.remoteUrl; }
  get remoteAiringId()   { return this._syncChannel.remoteAiringId; }
  get remoteProgress()   { return this._syncChannel.remoteProgress; }
  get remoteTabId()      { return this._syncChannel.remoteTabId; }
  get remoteWindowName() { return this._syncChannel.remoteWindowName; }
  get isPlayingRemotely() { return this._syncChannel.isPlayingRemotely; }
  get isPlayingLocally()  { return this._syncChannel.isPlayingLocally; }
  get isPlayingAnywhere() { return this._syncChannel.isPlayingAnywhere; }

  /**
   * Send a command to other tabs.
   * @param {'pause'|'play'|'toggle'} command
   * @param {string} [targetTabId] - Pass this.remoteTabId to target the tab that last played.
   */
  sendCommand(command, targetTabId = null, details = {}) {
    this._syncChannel.sendCommand(command, targetTabId, details);
  }

  // A player on this origin shares the channel, one on the station's player domain is reached through
  // the relay, and one on another site only once the window this tab opened has answered.
  canReach(playerUrl) {
    let origin = new URL(playerUrl, window.location.href).origin;
    return origin === window.location.origin || this._syncChannel.relayOrigin === origin || this._syncChannel.directOrigin === origin;
  }

  openPlayerWindow(url, windowName, features) {
    return this._syncChannel.openWindow(url, windowName, features);
  }

  focusPlayerWindow() {
    return this._syncChannel.focusWindow();
  }

  // Optional seam: the embeddable registers a `navigation` service that can open an episode by id.
  async openEpisodeFromRemote({ episodeId, at }) {
    let navigation = getOwner(this)?.lookup('service:navigation');
    if (!navigation || !episodeId) return;
    // Or the outgoing sound plays on while the new one loads.
    this.stereo.currentSound?.pause();
    await navigation.openEpisode(episodeId, { at, play: true });
  }

  goLiveFromRemote() {
    this.stereo.currentSound?.pause();
    this.feeds.goLive();
  }

  _lastProgressAt = 0;

  reportProgress(feed) {
    if (Date.now() - this._lastProgressAt < 5000) return;
    let sound = feed.loadedSound;
    if (!sound || !feed.transportIsPlaying) return;
    this._lastProgressAt = Date.now();
    this.broadcastSoundState(sound, true);
  }

  broadcastSoundState(sound, isPlaying) {
    let feed = sound ? this.feeds.findFeed({ url: sound.url }) : null;
    let isArchive = feed?.kind === 'playback';
    this._syncChannel.broadcastState({
      isPlaying,
      url: sound?.url,
      airingId: isArchive ? feed.airing?.id ?? null : null,
      progress: isArchive ? this.progressThrough(feed) : null,
      windowName: window.name || null,
    });
  }

  // The airing window, as on the scrubber. A recording in progress has no sound duration.
  progressThrough(feed) {
    let { actualStartsAt, startsAt, actualEndsAt, endsAt } = feed.airing ?? {};
    let start = actualStartsAt || startsAt;
    let end = actualEndsAt || endsAt;
    let duration = start && end ? new Date(end) - new Date(start) : feed.transportDuration;
    let position = feed.playheadPositionMs ?? feed.transportPosition;
    if (!duration || !Number.isFinite(duration) || !Number.isFinite(position)) return null;
    return Math.min(1, Math.max(0, position / duration));
  }

  // ---------------------------------------------------------------------------
  // Setup
  // ---------------------------------------------------------------------------

  addStereoMonitors() {
    this._onAudioPaused = this.onAudioPaused.bind(this);
    this._onAudioPlayed = this.onAudioPlayed.bind(this);
    this._onAudioBlocked = this.onAudioBlocked.bind(this);
    this._onAudioEnded = this.onAudioEnded.bind(this);

    this.stereo.on('audio-played', this._onAudioPlayed);
    this.stereo.on('audio-paused', this._onAudioPaused);
    this.stereo.on('audio-ended', this._onAudioEnded);
    this.stereo.on('audio-blocked', this._onAudioBlocked);
  }

  // ---------------------------------------------------------------------------
  // Stereo event handlers
  // ---------------------------------------------------------------------------

  onAudioPlayed({ sound }) {
    this._closed = false;
    this.currentBroadcastFeed()?.recordListeningSessionTask.perform();
    this.broadcastSoundState(sound, true);
  }

  onAudioPaused({ sound }) {
    // Remember where the listener stopped so the scrubber reloads here.
    this.currentPlaybackFeed()?.persistResumePosition();
    // A feed switch pauses the outgoing sound after the incoming one has played.
    if (this.stereo.currentSound && sound !== this.stereo.currentSound) return;
    this.broadcastSoundState(sound, false);
  }

  // The single end-of-playback choke point: mark the finished airing fully
  // listened (full progress bar, no resume at the very end), then advance the
  // play queue to the next airing if one is queued.
  onAudioEnded() {
    let feed = this.currentPlaybackFeed();

    // Reaching the end of an airing that's still on the air means catching up
    // to the live edge, not finishing the show: the rest hasn't been broadcast
    // yet. Hand back to the live stream rather than mark it listened (which
    // clears the resume position) and advance past it.
    if (feed?.isInProgress) {
      this.feeds.goLive();
      return;
    }

    feed?.markListened();
    this.advanceQueue();
    this.broadcastSoundState(this.stereo.currentSound, false);
  }

  // Optional queue seam: if a host registered a `playback-queue` service, play
  // its next airing when the current one finishes. Inert when none is present.
  advanceQueue() {
    try {
      let queue = getOwner(this)?.lookup('service:playback-queue');
      queue?.advance();
    } catch (e) {
      // no queue service registered — nothing to advance
    }
  }

  currentPlaybackFeed() {
    return this.currentFeedOfKind('playback');
  }

  currentBroadcastFeed() {
    return this.currentFeedOfKind('broadcast');
  }

  currentFeedOfKind(kind) {
    let sound = this.stereo.currentSound;
    if (!sound) return null;
    let feed = this.feeds.findFeed({ url: sound.url });
    return feed?.kind === kind ? feed : null;
  }

  onAudioBlocked({ sound }) {
    this.blockedSound = sound;
    this.broadcastSoundState(sound, false);
    this.handleAudioBlocked.perform(sound);

    this.stereo.one('audio-played', () => {
      this.blockedSound = null;
      if (this.blockedPrompt) {
        hideAll();
        this.blockedPrompt = null;
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Teardown
  // ---------------------------------------------------------------------------

  teardown() {
    if (!this.stereo.isDestroyed) {
      try {
        this.stereo.off('audio-played', this._onAudioPlayed);
        this.stereo.off('audio-blocked', this._onAudioBlocked);
        this.stereo.off('audio-paused', this._onAudioPaused);
        this.stereo.off('audio-ended', this._onAudioEnded);
      } catch (e) {
        // no op
      }
    }
    this._syncChannel.destroy();
  }

  // ---------------------------------------------------------------------------
  // Playback actions
  // ---------------------------------------------------------------------------

  stopAndClose() {
    this.stereo.stop();
    this._closed = true;
  }

  handleAudioBlocked = task({ restartable: true }, async sound => {
    let query = `[data-identifier="${normalizeIdentifier(
      sound.url,
    )}"] .dubbletrack-player-controls`;
    var elements;
    var waitLength = 0;
    var pauseSize = 200;
    while (true) {
      elements = document.querySelectorAll(query);
      waitLength = waitLength + pauseSize;

      await timeout(pauseSize);
      if ((elements && elements.length > 0) || waitLength > 3000) {
        break;
      }
    }

    let instances = tippy(elements, {
      role: 'tooltip',
      theme: 'attention',
      placement: 'bottom',
      maxWidth: '200px',
      animation: 'shift-away-subtle',
      hideOnClick: true,
      interactive: false,
      content:
        'Your browser blocked our autoplay attempt, so give this button a click to listen',
      trigger: 'manual',
      allowHTML: true,
    });

    this.blockedPrompt = instances[0];
    this.blockedPrompt?.show();
    await timeout(1000);
  });
}
